import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LayoutDashboard, Calendar, Users, MessageSquare, HelpCircle, FileText, Menu, Bell, Search, LogOut, KeyRound, ShieldCheck, History, Settings } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import SessionExpiryBanner from "@/components/SessionExpiryBanner";

function pageTitleFromPath(pathname) {
  if (pathname === "/") return "Guida";
  if (pathname.startsWith("/ticket/")) return "Dettaglio ticket";
  if (pathname === "/ticket") return "Cruscotto ticket";
  if (pathname === "/calendario") return "Calendario";
  if (pathname === "/anagrafiche") return "Anagrafiche";
  if (pathname === "/feedback") return "Feedback";
  if (pathname === "/ipotesi") return "Note applicative";
  if (pathname === "/admin/utenti") return "Gestione utenti";
  if (pathname === "/admin/audit") return "Audit log";
  return "";
}

function SidebarBrand() {
  return (
    <Link to="/" className="flex items-center gap-3 px-2 mb-6">
      <div className="emmepi-logo-mark">M</div>
      <div className="flex flex-col leading-tight">
        <span className="text-white emmepi-logo-text text-[15px]">emmepigroup</span>
        <span className="text-blue-300 text-[10px] uppercase tracking-wider">Service · v1.0</span>
      </div>
    </Link>
  );
}

function SidebarNav({ onNavigate, role }) {
  const isOwner = role === "OWNER";
  const items = [
    { to: "/", label: "Guida", icon: HelpCircle, end: true },
    { to: "/ticket", label: "Ticket", icon: LayoutDashboard },
    { to: "/calendario", label: "Calendario", icon: Calendar },
    { to: "/anagrafiche", label: "Anagrafiche", icon: Users },
    { to: "/feedback", label: "Feedback", icon: MessageSquare },
  ];
  const ownerItems = [
    { to: "/admin/utenti", label: "Utenti & ruoli", icon: ShieldCheck },
    { to: "/admin/audit", label: "Audit log", icon: History },
  ];
  return (
    <nav className="flex flex-col gap-1">
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink key={to} to={to} end={end} data-testid={`nav-${to.replace('/', '') || 'home'}`} onClick={onNavigate}
          className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}>
          <Icon className="w-4 h-4 sidebar-icon" /><span>{label}</span>
        </NavLink>
      ))}
      {isOwner && (
        <>
          <div className="text-[10px] uppercase tracking-wider text-blue-300 mt-4 mb-1 px-3">Amministrazione</div>
          {ownerItems.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} data-testid={`nav-${to.replace(/\//g,'-').replace(/^-/,'')}`} onClick={onNavigate}
              className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}>
              <Icon className="w-4 h-4 sidebar-icon" /><span>{label}</span>
            </NavLink>
          ))}
        </>
      )}
    </nav>
  );
}

function roleLabel(r) {
  return { OWNER: "Owner", SERVICE_MANAGER: "Service manager", VIEWER: "Visualizzatore" }[r] || r;
}
function roleColor(r) {
  return { OWNER: "bg-purple-100 text-purple-800 border-purple-200",
           SERVICE_MANAGER: "bg-blue-100 text-blue-800 border-blue-200",
           VIEWER: "bg-slate-100 text-slate-700 border-slate-200" }[r] || "bg-slate-100";
}

export default function DemoLayout() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const location = useLocation();
  const nav = useNavigate();
  const title = pageTitleFromPath(location.pathname);
  const initials = (user?.nome || "?").split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase();

  const submitSearch = (e) => {
    e.preventDefault();
    const term = searchQ.trim();
    const path = location.pathname || "";
    // Ricerca contestuale: se sono su /anagrafiche, resto lì con ?q; altrimenti vado ai ticket
    if (path.startsWith("/anagrafiche")) {
      nav(term ? `/anagrafiche?q=${encodeURIComponent(term)}` : "/anagrafiche");
    } else if (path.startsWith("/calendar")) {
      nav(term ? `/calendar?q=${encodeURIComponent(term)}` : "/calendar");
    } else if (path.startsWith("/feedback")) {
      nav(term ? `/feedback?q=${encodeURIComponent(term)}` : "/feedback");
    } else if (path.startsWith("/utenti")) {
      nav(term ? `/utenti?q=${encodeURIComponent(term)}` : "/utenti");
    } else if (path.startsWith("/audit")) {
      nav(term ? `/audit?q=${encodeURIComponent(term)}` : "/audit");
    } else {
      nav(term ? `/ticket?q=${encodeURIComponent(term)}` : "/ticket");
    }
  };

  // Placeholder contestuale
  const searchPlaceholder = (() => {
    const path = location.pathname || "";
    if (path.startsWith("/anagrafiche")) return "Cerca nella tab attiva (clienti / stabilimenti / contatti / tecnici)… (invio)";
    if (path.startsWith("/calendar")) return "Cerca nel calendario… (invio)";
    if (path.startsWith("/feedback")) return "Cerca feedback… (invio)";
    return "Cerca ticket, cliente, stabilimento… (invio)";
  })();

  return (
    <div className="min-h-screen flex bg-[color:var(--page-bg)]">
      <aside className="hidden md:flex sidebar-dark w-60 flex-col p-4 sticky top-0 h-screen">
        <SidebarBrand />
        <SidebarNav role={user?.role} />
        <div className="mt-auto pt-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="avatar-circle">{initials}</div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-semibold truncate">{user?.nome}</div>
              <div className="text-blue-300 text-[11px] truncate">{roleLabel(user?.role)}</div>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <SessionExpiryBanner />
        <header className="topbar">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="sidebar-dark border-0 p-4 w-72 text-white">
              <SidebarBrand />
              <SidebarNav role={user?.role} onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <h1 className="text-lg md:text-xl font-bold text-slate-900 truncate">{title}</h1>
          <form className="topbar-search hidden sm:block" onSubmit={submitSearch}>
            <Search className="topbar-search-icon w-4 h-4" />
            <input type="text" placeholder={searchPlaceholder} value={searchQ} onChange={e => setSearchQ(e.target.value)} data-testid="global-search" />
          </form>
          <div className="ml-auto flex items-center gap-2 md:gap-3">
            <button className="relative p-2 hover:bg-slate-100 rounded-full" aria-label="Notifiche">
              <Bell className="w-5 h-5 text-slate-600" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1 hover:bg-slate-100 rounded-full" data-testid="user-menu">
                  <div className="avatar-circle w-8 h-8 text-[12px]">{initials}</div>
                  <div className="hidden md:flex flex-col items-start min-w-0">
                    <span className="text-sm font-semibold truncate">{user?.nome}</span>
                    <span className={`text-[10px] px-1.5 rounded border ${roleColor(user?.role)}`}>{roleLabel(user?.role)}</span>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm">{user?.nome}</div>
                  <div className="text-xs text-slate-500">{user?.username}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/change-password")} data-testid="menu-change-pwd">
                  <KeyRound className="w-4 h-4 mr-2" /> Cambia password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="menu-logout" className="text-red-600 focus:text-red-700">
                  <LogOut className="w-4 h-4 mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 min-w-0">
          <Outlet key={location.pathname} />
        </main>
      </div>
      <Toaster richColors position="top-right" />
    </div>
  );
}
